import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Clock, Loader2, Sparkles, Beef, Users, Tag, Trophy } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { IntelCard } from "@/components/IntelCard";
import { RunDiscoveryButton } from "@/components/AthleteCard";
import { relativeDate, exactDate, rankEvents } from "@/lib/format";

export default function SubjectDetail({ kind }) {
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [polling, setPolling] = useState(false);

  const load = async () => {
    const d = kind === "animal" ? await api.animalIntel(id) : await api.contractorIntel(id);
    setData(d);
    return d;
  };

  useEffect(() => {
    load().then((d) => {
      if (d.discovery_status === "running") {
        const started = d.subject?.discovery_started_epoch || 0;
        if (Date.now() / 1000 - started < 900) setPolling(true);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, kind]);

  useEffect(() => {
    if (!polling) return;
    const t = setInterval(async () => {
      const d = await load();
      if (d.discovery_status !== "running") {
        setPolling(false);
        if (d.discovery_status === "done") toast.success("News updated");
        if (d.discovery_status === "error") toast.error("Search failed — try again");
      }
    }, 5000);
    return () => clearInterval(t);
  }, [polling]);

  const run = async () => {
    if (kind === "animal") await api.discoverAnimal(id);
    else await api.discoverContractor(id);
    setData((p) => ({ ...p, discovery_status: "running" }));
    setPolling(true);
    toast.info("Searching public sources & news — up to a couple of minutes");
  };

  if (!data) {
    return (
      <div className="flex items-center justify-center py-32 text-[#686B77]">
        <Loader2 className="h-6 w-6 animate-spin mr-2" /> Loading…
      </div>
    );
  }

  const s = data.subject;
  const running = data.discovery_status === "running";
  const events = rankEvents(data.events || []);

  return (
    <div className="max-w-[1500px] mx-auto px-5 md:px-8 py-8">
      <button
        data-testid="back-button"
        onClick={() => nav("/stock")}
        className="flex items-center gap-2 text-sm text-[#9A9CA6] hover:text-[#F3EFE6] transition-colors mb-5"
      >
        <ArrowLeft className="h-4 w-4" /> Stock
      </button>

      <section className="rounded-2xl border border-[#262933] bg-[#12141A] p-6 md:p-8 mb-6">
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-5">
          <div className="min-w-0">
            <div className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.25em] text-[#E8833A] mb-2">
              {kind === "animal" ? <Beef className="h-3.5 w-3.5" /> : <Users className="h-3.5 w-3.5" />}
              {kind === "animal" ? s.animal_type : "Stock Contractor"}
            </div>
            <h1 className="font-display font-extrabold text-4xl sm:text-5xl tracking-tight text-[#F3EFE6]">
              {s.name}
            </h1>

            {kind === "animal" ? (
              <div className="flex flex-wrap items-center gap-x-5 gap-y-2 mt-3 text-sm text-[#B8BAC4]">
                <span className="flex items-center gap-1.5">
                  <Tag className="h-4 w-4 text-[#686B77]" /> {s.brand_code} {s.brand_number}
                </span>
                <span className="text-[#9A9CA6]">Owner / Contractor: <span className="text-[#F3EFE6]">{s.contractor}</span></span>
                {s.appearances_count > 0 && (
                  <span className="text-[#9A9CA6]">Season: <span className="text-[#F3EFE6]">{s.appearances_count} draws</span>{s.total_won > 0 && <span className="text-[#E8833A]"> · ${Math.round(s.total_won).toLocaleString()} won</span>}</span>
                )}
              </div>
            ) : (
              <div className="flex flex-wrap items-center gap-x-5 gap-y-2 mt-3 text-sm text-[#B8BAC4]">
                <span className="text-[#9A9CA6]">Brands: <span className="font-mono text-[#F3EFE6]">{(s.brands || []).join(", ")}</span></span>
                <span className="text-[#9A9CA6]">{(s.animals || []).length} head of stock</span>
              </div>
            )}

            <div className="flex flex-wrap gap-1.5 mt-3">
              {(s.events || []).map((e) => (
                <span key={e} className="px-2.5 py-1 rounded-md text-[11px] font-mono uppercase tracking-wider font-semibold border border-[#E8833A]/40 bg-[#E8833A]/10 text-[#E8833A]">
                  {e}
                </span>
              ))}
            </div>

            {kind === "animal" && (s.draws || []).length > 0 && (
              <div className="mt-4 flex flex-wrap gap-2">
                {s.draws.map((d, i) => (
                  <span key={i} className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-[#1A1D26] border border-[#262933] text-xs text-[#B8BAC4]">
                    <Trophy className="h-3 w-3 text-[#E8833A]" /> Drawn on {d.contestant} · {d.event}
                  </span>
                ))}
              </div>
            )}
          </div>

          <div className="flex flex-col items-start lg:items-end gap-3 shrink-0">
            <RunDiscoveryButton onClick={run} status={data.discovery_status} />
            {running && s.discovery_stage ? (
              <div className="font-mono text-[11px] text-[#E8833A] flex items-center gap-1.5" data-testid="discovery-stage">
                <Loader2 className="h-3 w-3 animate-spin" /> {s.discovery_stage}
              </div>
            ) : (
              <div className="font-mono text-[11px] text-[#686B77] flex items-center gap-1.5" data-testid="last-scan-date">
                <Clock className="h-3 w-3" />
                {data.last_discovery ? `Last searched ${relativeDate(data.last_discovery)} · ${exactDate(data.last_discovery)}` : "Not searched yet"}
              </div>
            )}
          </div>
        </div>

        {kind === "contractor" && (s.animals || []).length > 0 && (
          <div className="mt-5 pt-5 border-t border-[#1E212A]">
            <div className="font-mono text-[10px] uppercase tracking-widest text-[#686B77] mb-2">Stock in their pen</div>
            <div className="flex flex-wrap gap-1.5">
              {s.animals.map((a) => (
                <Link key={a.id} to={`/animal/${a.id}`} className="px-2 py-0.5 rounded-md bg-[#1A1D26] border border-[#262933] text-xs text-[#B8BAC4] hover:border-[#E8833A]/50 hover:text-[#F3EFE6] transition-colors">
                  {a.name}
                </Link>
              ))}
            </div>
          </div>
        )}
      </section>

      {events.length === 0 ? (
        <div className="text-center py-20 rounded-xl border border-dashed border-[#262933] bg-[#0F1116]">
          {running ? (
            <>
              <Loader2 className="h-8 w-8 text-[#E8833A] animate-spin mx-auto mb-4" />
              <p className="text-[#F3EFE6] font-display text-lg">{s.discovery_stage || "Searching public sources & news…"}</p>
            </>
          ) : (
            <>
              <Sparkles className="h-8 w-8 text-[#686B77] mx-auto mb-4" />
              <p className="text-[#F3EFE6] font-display text-lg">No news gathered yet</p>
              <p className="text-[#9A9CA6] text-sm mt-1 mb-5">Search public sources for verified facts about {s.name}.</p>
              <div className="flex justify-center"><RunDiscoveryButton onClick={run} status={data.discovery_status} /></div>
            </>
          )}
        </div>
      ) : (
        <>
          <div className="flex items-center gap-2 mb-4">
            <Trophy className="h-5 w-5 text-[#E8833A]" />
            <h2 className="font-display font-bold text-xl text-[#F3EFE6]">What we found</h2>
            <span className="font-mono text-xs text-[#686B77]">{events.length}</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {events.map((ev) => <IntelCard key={ev.id} ev={ev} />)}
          </div>
        </>
      )}
    </div>
  );
}
